import { useCallback, useState } from 'react'
import cx from 'classnames'
import { generatePath, Link } from 'react-router-dom'

import { ChannelInfo } from 'types'
import { truncate } from 'utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ReactComponent as MembersIcon } from 'screens/Groups/icons/members.svg'

import Styles from './GroupBreadcrumbs.module.scss'
import { GROUPS_PATH, GROUP_PATH } from 'paths'
import TitleEmojiPicker from 'components/ActionBar/ArticleTitleBar/TitleEmojiPicker/TitleEmojiPicker'

interface Props {
  workspaceId: string | number
  group: ChannelInfo
  onEmojiUpdate: (emoji: string | null) => void
}

export const GroupBreadcrumbs = ({ workspaceId, group, onEmojiUpdate }: Props) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const { isMobile } = useResponsiveDesign()

  const toggleShowEmojiPicker = useCallback(() => {
    if (!group.owner) return

    if (showEmojiPicker) {
      setShowEmojiPicker(false)
      return
    }

    setShowEmojiPicker(true)
  }, [group.owner, showEmojiPicker])

  const emojiPickerContainer = (onChannelUpdate: (emoji: string | null) => void, emoji?: string | null) => {
    return (
      <TitleEmojiPicker
        onSelect={(emoji) => {
          onChannelUpdate(emoji?.native || null)
        }}
        removable={!!emoji}
        showEmojiPicker={showEmojiPicker}
        toggleShowEmojiPicker={toggleShowEmojiPicker}
      />
    )
  }

  const groupPresent = group?.id && group?.name

  const docIcon = (emoji: string | null, canEdit: boolean) => {
    return <div className={cx(Styles.emoji, { [Styles.editable]: canEdit })}>{!!emoji ? emoji : <MembersIcon />}</div>
  }

  return (
    <div className={Styles.breadcrumbs}>
      <Link
        className={cx(Styles.groupLink, { [Styles.active]: !groupPresent })}
        to={generatePath(GROUPS_PATH, { workspaceId: workspaceId })}
      >
        <MembersIcon />
        Groups
      </Link>

      {groupPresent && (
        <div className={Styles.groupContainer}>
          <span className={Styles.separator}>/</span>
          <Link
            className={cx(Styles.groupLink, { [Styles.active]: groupPresent })}
            to={generatePath(GROUP_PATH, { workspaceId: workspaceId, id: group.id })}
          >
            {emojiPickerContainer(onEmojiUpdate, group.emoji)}
            <button
              className={cx(Styles.emojiContainer, { [Styles.editable]: group.owner })}
              onClick={(e) => {
                e.stopPropagation()
                if (!group.owner) return
                setShowEmojiPicker(true)
              }}
            >
              {docIcon(group.emoji, group.owner)}
            </button>
            {isMobile ? truncate(group.name, 30) : group.name}
          </Link>
        </div>
      )}
    </div>
  )
}
